/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { Fragment, useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { jsx } from '@atlaskit/css';
import PageIcon from '@atlaskit/icon/core/page';
import { token } from '@atlaskit/tokens';
import { SearchResult } from '@atlassian/search-dialog/search-result';

import { messages } from '../messages';
import type { articleResultsQuery } from './__generated__/articleResultsQuery.graphql';
import {
	ExternalLinkComponent,
	InternalLinkComponent,
	SearchResultContainer,
	SearchResultSection,
} from './search-result-section';
import { SearchResultStatus, SearchResultType, type SearchQueryProps } from './types';
import { useSearchResultAnalytics } from './use-search-result-analytics';

const DEFAULT_RESULTS_LIMIT = 3;

const getResultStatus = (
	errorMessage: string | null | undefined,
	resultCount: number,
): SearchResultStatus => {
	if (errorMessage) {
		return SearchResultStatus.Error;
	}
	if (resultCount > 0) {
		return SearchResultStatus.Results;
	}
	return SearchResultStatus.Empty;
};

export const ArticleResults = ({
	cloudId,
	helpCenterAri,
	onStatus,
	queryWord,
}: SearchQueryProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const data = useLazyLoadQuery<articleResultsQuery>(
		graphql`
			query articleResultsQuery(
				$cloudId: ID!
				$helpCenterAri: ID
				$queryTerm: String!
				$limit: Int!
			) {
				helpObjectStore_searchArticles(
					cloudId: $cloudId
					queryTerm: $queryTerm
					limit: $limit
					helpCenterAri: $helpCenterAri
					highlight: false
				) {
					... on HelpObjectStoreArticleSearchResults {
						results {
							ari
							title
							displayLink
							metadata {
								isExternal
							}
						}
					}
					... on HelpObjectStoreSearchError {
						message
					}
				}
			}
		`,
		{
			cloudId,
			helpCenterAri,
			queryTerm: queryWord,
			limit: DEFAULT_RESULTS_LIMIT,
		},
	);
	const errorMessage = data.helpObjectStore_searchArticles?.message;
	const articles = useMemo(
		() => data.helpObjectStore_searchArticles?.results ?? [],
		[data.helpObjectStore_searchArticles?.results],
	);
	const resultStatus = getResultStatus(errorMessage, articles.length);

	useSearchResultAnalytics({
		resultCount: articles.length,
		resultStatus,
		resultType: SearchResultType.Articles,
	});

	useEffect(() => {
		onStatus(resultStatus);
	}, [onStatus, resultStatus]);

	if (articles.length === 0 || errorMessage) {
		return <Fragment />;
	}

	return (
		<SearchResultSection heading={formatMessage(messages.resourcesHeading)}>
			{articles.map((article) => (
				<SearchResultContainer key={article.ari}>
					<SearchResult
						href={article.displayLink}
						linkComponent={
							article.metadata?.isExternal ? ExternalLinkComponent : InternalLinkComponent
						}
						title={article.title}
						isCollapsed
						icon={<PageIcon label="" spacing="spacious" color={token('color.text.subtle')} />}
					/>
				</SearchResultContainer>
			))}
		</SearchResultSection>
	);
};
